import React, { useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import PantherHead from '../assets/PantherHead.png';
export const LOADER_SIZES = Object.freeze({
  XSMALL: "x-small",
  SMALL: "small",
  MEDIUM: "medium",
  LARGE: "large",
});

const PinkPantherLoader = ({ fullHeight = false, size, withoutText = false }) => {
  const { t } = useTranslation("general");
  const canvasRef = useRef(null);
  const gameStateRef = useRef({
    gameState: 'playing',
    score: 0,
    gameSpeed: 2.5,
    panther: {
      x: 60,
      y: 0,
      width: 35,
      height: 35,
      velY: 0,
      jumping: false,
      grounded: true
    },
    obstacles: [],
    obstacleTimer: 0,
    backgroundElements: []
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const gameWidth = 600;
    const gameHeight = 200;
    canvas.width = gameWidth;
    canvas.height = gameHeight;

    const groundY = gameHeight - 50;
    gameStateRef.current.panther.y = groundY - gameStateRef.current.panther.height;

    // Pink Panther head image
    const pantherImg = new Image();
    pantherImg.crossOrigin = "anonymous";
    pantherImg.src = PantherHead;

    // Initialize background servers
    for (let i = 0; i < 8; i++) {
      gameStateRef.current.backgroundElements.push({
        x: i * 100,
        y: Math.random() * (gameHeight - 120) + 20,
        width: 20,
        height: 60,
        speed: 0.5
      });
    }

    const jump = () => {
      const state = gameStateRef.current;
      if (state.gameState === 'playing' && state.panther.grounded) {
        state.panther.velY = -12;
        state.panther.jumping = true;
        state.panther.grounded = false;
      } else if (state.gameState === 'gameOver') {
        // Reset game on game over
        state.gameState = 'playing';
        state.score = 0;
        state.gameSpeed = 2.5;
        state.panther.y = groundY - state.panther.height;
        state.panther.velY = 0;
        state.panther.grounded = true;
        state.panther.jumping = false;
        state.obstacles.length = 0;
        state.obstacleTimer = 0;
      }
    };

    const drawPanther = () => {
      const panther = gameStateRef.current.panther;
      if (pantherImg.complete && pantherImg.naturalHeight !== 0) {
        ctx.drawImage(pantherImg, panther.x, panther.y, panther.width, panther.height);
      } else {
        // Fallback: simple pink circle with ears
        ctx.fillStyle = '#ff69b4';
        
        // Head
        ctx.beginPath();
        ctx.arc(panther.x + panther.width/2, panther.y + panther.height/2, panther.width/2, 0, Math.PI * 2);
        ctx.fill();
        
        // Ears
        ctx.beginPath();
        ctx.arc(panther.x + 8, panther.y + 5, 6, 0, Math.PI * 2);
        ctx.arc(panther.x + panther.width - 8, panther.y + 5, 6, 0, Math.PI * 2);
        ctx.fill();
        
        // Eyes
        ctx.fillStyle = '#000';
        ctx.fillRect(panther.x + 10, panther.y + 12, 3, 4);
        ctx.fillRect(panther.x + 22, panther.y + 12, 3, 4);
      }
    };

    const drawObstacle = (obs) => {
      // Server/Computer
      ctx.fillStyle = '#333';
      ctx.fillRect(obs.x, obs.y, obs.width, obs.height);
      
      // Server panels
      ctx.fillStyle = '#555';
      ctx.fillRect(obs.x + 2, obs.y + 5, obs.width - 4, 8);
      ctx.fillRect(obs.x + 2, obs.y + 18, obs.width - 4, 8);
      ctx.fillRect(obs.x + 2, obs.y + 31, obs.width - 4, 8);
      
      // Blinking lights
      const time = Date.now();
      if (Math.sin(time * 0.008) > 0) {
        ctx.fillStyle = '#00ff41';
        ctx.fillRect(obs.x + obs.width - 8, obs.y + 7, 3, 3);
        ctx.fillRect(obs.x + obs.width - 8, obs.y + 20, 3, 3);
        ctx.fillRect(obs.x + obs.width - 8, obs.y + 33, 3, 3);
      }
    };

    const drawBackground = () => {
      // Datacenter floor
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, groundY, gameWidth, gameHeight - groundY);
      
      // Floor grid pattern
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 1;
      for (let i = 0; i < gameWidth; i += 30) {
        ctx.beginPath();
        ctx.moveTo(i, groundY);
        ctx.lineTo(i, gameHeight);
        ctx.stroke();
      }
      
      // Background server racks
      gameStateRef.current.backgroundElements.forEach(elem => {
        ctx.fillStyle = '#222';
        ctx.fillRect(elem.x, elem.y, elem.width, elem.height);
        
        // Server lights
        ctx.fillStyle = '#004400';
        for (let i = 0; i < 2; i++) {
          ctx.fillRect(elem.x + 3, elem.y + 10 + i * 20, 2, 2);
          ctx.fillRect(elem.x + 12, elem.y + 10 + i * 20, 2, 2);
        }
      });
    };

    const updateGame = () => {
      const state = gameStateRef.current;
      if (state.gameState !== 'playing') return;

      // Update score
      state.score += 1;
      
      // Increase game speed gradually
      state.gameSpeed = 2.5 + Math.floor(state.score / 150) * 0.3;

      // Update panther physics
      if (!state.panther.grounded) {
        state.panther.velY += 0.5; // gravity
        state.panther.y += state.panther.velY;
        
        if (state.panther.y >= groundY - state.panther.height) {
          state.panther.y = groundY - state.panther.height;
          state.panther.grounded = true;
          state.panther.jumping = false;
          state.panther.velY = 0;
        }
      }

      // Spawn obstacles
      state.obstacleTimer++;
      if (state.obstacleTimer > 80 - Math.floor(state.score / 300)) {
        state.obstacles.push({
          x: gameWidth,
          y: groundY - 45,
          width: 25,
          height: 45
        });
        state.obstacleTimer = 0;
      }

      // Update obstacles
      for (let i = state.obstacles.length - 1; i >= 0; i--) {
        state.obstacles[i].x -= state.gameSpeed;
        
        if (state.obstacles[i].x + state.obstacles[i].width < 0) {
          state.obstacles.splice(i, 1);
          continue;
        }
        
        // Collision detection
        if (state.panther.x < state.obstacles[i].x + state.obstacles[i].width - 5 &&
            state.panther.x + state.panther.width > state.obstacles[i].x + 5 &&
            state.panther.y < state.obstacles[i].y + state.obstacles[i].height - 5 &&
            state.panther.y + state.panther.height > state.obstacles[i].y + 5) {
          state.gameState = 'gameOver';
        }
      }

      // Update background elements
      state.backgroundElements.forEach(elem => {
        elem.x -= 0.3;
        if (elem.x + elem.width < 0) {
          elem.x = gameWidth;
          elem.y = Math.random() * (gameHeight - 120) + 20;
        }
      });
    };

    let animationFrame;
    const gameLoop = () => {
      // Clear canvas
      ctx.clearRect(0, 0, gameWidth, gameHeight);
      
      // Draw everything
      drawBackground();
      drawPanther();
      gameStateRef.current.obstacles.forEach(drawObstacle);
      
      // Update game logic
      updateGame();
      
      animationFrame = requestAnimationFrame(gameLoop);
    };

    // Event handlers
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        jump();
      }
    };

    const handleClick = () => jump();

    document.addEventListener('keydown', handleKeyDown);
    canvas.addEventListener('click', handleClick);

    // Start the game
    gameLoop();

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      canvas.removeEventListener('click', handleClick);
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, []);

  // Size classes based on your original loader
  const sizeClass =
    size === LOADER_SIZES.XSMALL ? 'x-small' :
    size === LOADER_SIZES.SMALL ? 'small' :
    size === LOADER_SIZES.MEDIUM ? 'medium' : '';

  const containerStyle = {
    position: fullHeight ? 'fixed' : 'relative',
    top: fullHeight ? 0 : 'auto',
    left: fullHeight ? 0 : 'auto',
    width: fullHeight ? '100%' : 'auto',
    height: fullHeight ? '100%' : 'auto',
    backgroundColor: fullHeight ? 'rgba(0, 0, 0, 0.8)' : 'transparent',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: fullHeight ? 9999 : 'auto'
  };

  const gameContainerStyle = {
    width: size === LOADER_SIZES.XSMALL ? '300px' : 
           size === LOADER_SIZES.SMALL ? '400px' : '600px',
    height: size === LOADER_SIZES.XSMALL ? '100px' : 
            size === LOADER_SIZES.SMALL ? '150px' : '200px',
    position: 'relative',
    background: 'linear-gradient(to bottom, #0f3460 0%, #16537e 50%, #1a1a2e 100%)',
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    marginBottom: withoutText ? '0' : '20px'
  };

  return (
    <div style={containerStyle}>
      <div style={gameContainerStyle}>
        <canvas 
          ref={canvasRef}
          style={{
            display: 'block',
            borderRadius: '10px',
            width: '100%',
            height: '100%'
          }}
        />
        
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '15px',
          color: '#00ff41',
          fontSize: size === LOADER_SIZES.XSMALL ? '8px' : '12px',
          textShadow: '0 0 5px #00ff41',
          zIndex: 10
        }}>
          <div>Score: {gameStateRef.current?.score || 0}</div>
        </div>

        <div style={{
          position: 'absolute',
          bottom: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#00ff41',
          fontSize: size === LOADER_SIZES.XSMALL ? '6px' : '8px',
          textAlign: 'center',
          textShadow: '0 0 5px #00ff41'
        }}>
          <div>Click or SPACE to jump!</div>
        </div>
      </div>
      
      {!withoutText && (
        <p style={{
          color: '#00ff41',
          fontSize: '16px',
          textShadow: '0 0 10px #00ff41',
          margin: '10px 0 0 0',
          textAlign: 'center'
        }}>
          {t("loading")}
        </p>
      )}
    </div>
  );
};

export default PinkPantherLoader;